<template>
  <div class="page-keywords">
    <BaseLoadingBar is-small v-if="pending && !categories.length" />
    <div v-else-if="!pending && !categories.length" class="keywords__empty">
      <LargeNotice
        v-on:click="openAddCategory"
        :title="$t('page.admin-keywords.no-keywords-title')"
        :subtitle="$t('page.admin-keywords.no-keywords-subtitle')"
        :buttonText="$t('page.admin-keywords.new-category')"
        :buttonIcon="'arrow-right'"
      />
    </div>
    <div v-else class="page-keywords__content">
      <!-- Phones: its main action, as the "+" of the settings header. -->
      <PageMainAction :label="$t('page.admin-keywords.new-category')" @click="openAddCategory" />
      <div v-if="!isPhone" class="page-admin__table-header">
        <div class="page-admin__actions">
          <BaseRefreshButton :size="40" :pending="pending" @refresh="onRefreshKeywords" />
          <BaseButton @click.native="openAddCategory" has-icon-left>
            <BaseIcon name="plus" />
            {{ $t('page.admin-keywords.add-category') }}
          </BaseButton>
        </div>
      </div>
      <language-tab :languages="languages" :tabIndex="tabIndex" :onchange="onTabChange"></language-tab>
      <!-- Phones: the count line (docs/page-layout.md), the categories under it. -->
      <BasePhoneCountLine v-if="isPhone" class="page-keywords__count-line">
        <template #count>
          <span class="number-monospace">{{ keywordCount }}</span>
          {{ ' ' + $tc('basic-words.keywords', keywordCount) }}
        </template>
        <template #actions>
          <BaseRefreshButton :pending="pending" @refresh="onRefreshKeywords" />
        </template>
      </BasePhoneCountLine>
      <div class="page-keywords__content-main">
        <div v-for="category in currentCategories" :key="category.id">
          <KeywordsList
            :category="category"
            @on-add-keyword="onAddKeywords"
            @on-category-edit="openEditCategory"
            @on-category-delete="openDeleteCategory"
            @on-keyword-edit="openEditKeyword"
          >
          </KeywordsList>
          <KeywordsAddPanel
            v-if="editKeywordsCategory.id === category.id"
            :category-name="category.name"
            @close="editKeywordsCategory = {}"
            @opened="focusKeywordInput"
          >
            <ResizeAuto>
              <template v-slot:default="{ resize }">
                <textarea
                  ref="textAreaRef"
                  @input="resize"
                  :placeholder="$t('page.admin-keywords.type-keyword')"
                  v-model="rawKeywords"
                  cols="40"
                  rows="1"
                  class="page-keywords__textarea"
                ></textarea>
              </template>
            </ResizeAuto>
            <div v-if="toAdd.alreadyThere" class="page-keywords__add-hint">
              {{ $tc('page.admin-keywords.already-in-category', toAdd.alreadyThere, { count: toAdd.alreadyThere }) }}
            </div>
            <div class="page-keywords__add-keyword-group">
              <BaseShortcut
                value="~"
                isActionable
                @shortcut-clicked="addTilde()"
                v-tooltip="{ content: $t('search.tild'), placement: 'top', delay: 500 }"
              />
              <BaseButton
                isSmall
                has-icon-right
                :color="transformState === 'idle' ? 'primary' : 'gray'"
                :disabled="transformState !== 'idle' || !editKeywordsCategory.id"
                :isLoading="transformState === 'loading'"
                @click.native="transformOnly()"
              >
                <span class="btn-label">{{ $t('actions.transform') }}</span>
                <span
                  :class="['check-slot', { 'is-visible': transformState === 'done' && hasNonEmptyInput }]"
                  aria-hidden="true"
                >
                  <BaseIcon name="check" :size="12" class="check-icon" />
                </span>
              </BaseButton>

              <BaseButton
                @click.native="addKeywords()"
                :disabled="areKeywordsInvalid || !toAdd.keywords.length"
                isSmall
              >
                {{ $tc('page.admin-keywords.add-keywords', toAdd.keywords.length, { count: toAdd.keywords.length }) }}
              </BaseButton>
            </div>
            <div v-if="transformState === 'done'" class="mt-2 transform-result">
              <div v-if="transformError" class="page-keywords__error">{{ transformError }}</div>
            </div>
            <div v-if="areTheyKeywords && areKeywordsInvalid">
              <KeywordsErrors :validation="keywordsCriteria" color="error" />
            </div>
          </KeywordsAddPanel>
        </div>
      </div>
    </div>

    <AddCategoryDialog ref="addCategoryDialog" @action-succeeded="onGetKeywords" />
    <DeleteDialog ref="deleteCategoryDialog" @execute="onDeleteCategory" :buttonText="$t('actions.delete')">
      <template slot="content">
        <div class="page-keywords__delete-category">
          <i18n path="page.admin-keywords.delete-dialog.content">
            <template v-slot:name>
              <span class="page-keywords__category-name"> {{ categoryToDelete.name }}</span>
            </template>
          </i18n>
        </div>
        <div class="page-keywords__delete-category--notice">{{ $t('page.admin-keywords.delete-dialog.subtitle') }}</div>
      </template>
    </DeleteDialog>
    <EditCategoryDialog ref="editCategoryDialog" @action-succeeded="onRefreshKeywords" />
    <EditKeywordDialog ref="editKeywordDialog" @action-succeeded="onRefreshKeywords" />
  </div>
</template>

<script>
import AddCategoryDialog from '@/components/page-admin-keywords/AddCategoryDialog';
import BaseButton from '@/components/base/BaseButton';
import BaseIcon from '@/components/base/BaseIcon';
import BaseLoadingBar from '@/components/base/BaseLoadingBar';
import BasePhoneCountLine from '@/components/base/BasePhoneCountLine.vue';
import BaseRefreshButton from '@/components/base/BaseRefreshButton.vue';
import KeywordsErrors from '@/components/util/KeywordsErrors';
import KeywordsAddPanel from '@/components/page-admin-keywords/KeywordsAddPanel.vue';
import KeywordsList from '@/components/page-admin-keywords/KeywordsList';
import DeleteDialog from '@/components/page-admin-keywords/DeleteDialog';
import EditCategoryDialog from '@/components/page-admin-keywords/EditCategoryDialog';
import EditKeywordDialog from '@/components/page-admin-keywords/EditKeywordDialog';
import LanguageTab from '@/components/page-keywords/LanguageTab';
import LargeNotice from '@/components/notice/LargeNotice';
import ResizeAuto from '@/components/page-admin-keywords/ResizeAuto';
import BaseShortcut from '@/components/base/BaseShortcut.vue';
import PageMainAction from '@/components/layout/PageMainAction.vue';
import { viewport } from '@/util/viewport';
import { mapActions, mapGetters } from 'vuex';
import { validateKeywords, buildKeywords, keywordsToAdd } from '@/util/keywords-util';
import { keywordsService } from '@/services/keywords.service';

export default {
  name: 'PageAdminKeywords',
  components: {
    AddCategoryDialog,
    BaseButton,
    BaseIcon,
    BaseLoadingBar,
    BasePhoneCountLine,
    BaseRefreshButton,
    KeywordsErrors,
    KeywordsAddPanel,
    KeywordsList,
    DeleteDialog,
    EditCategoryDialog,
    EditKeywordDialog,
    LanguageTab,
    LargeNotice,
    ResizeAuto,
    BaseShortcut,
    PageMainAction,
  },
  data() {
    return {
      tabIndex: 0,
      categoryToDelete: {},
      editKeywordsCategory: {},
      rawKeywords: '',
      transformError: '',
      addLoading: false,
      transformState: 'idle',
      updatingFromTransform: false,
    };
  },
  computed: {
    ...mapGetters('keywords', ['pending', 'languages', 'categories']),
    isPhone() {
      return viewport.size === 'phone';
    },
    keywordCount() {
      const language = this.languages[this.tabIndex];
      return language ? language.keywordCount : 0;
    },
    currentCategories() {
      return this.languages.length > 0 && this.languages[this.tabIndex] ? this.languages[this.tabIndex].categories : [];
    },
    keywords() {
      return buildKeywords(this.rawKeywords);
    },
    toAdd() {
      const category = this.categories.find((cat) => cat.id === this.editKeywordsCategory.id);
      return keywordsToAdd(this.rawKeywords, category ? category.keywords.map((keyword) => keyword.value) : []);
    },
    keywordsCriteria() {
      return validateKeywords(this.toAdd.keywords);
    },
    areTheyKeywords() {
      return this.rawKeywords.length > 0;
    },
    areKeywordsInvalid() {
      return !this.keywordsCriteria.valid;
    },
    hasNonEmptyInput() {
      return !!(this.rawKeywords && this.rawKeywords.trim().length);
    },
  },
  watch: {
    rawKeywords() {
      if (this.transformState === 'done' && !this.updatingFromTransform) {
        this.transformState = 'idle';
      }
    },
  },
  methods: {
    ...mapActions('keywords', ['addKeywordsBulk', 'getKeywords', 'refreshKeywords', 'deleteCategory']),
    buildKeywords,
    validateKeywords,
    onTabChange(tabIndex) {
      this.tabIndex = tabIndex;
    },
    async onGetKeywords() {
      await this.getKeywords();
    },
    async onRefreshKeywords() {
      await this.refreshKeywords();
    },
    openAddCategory() {
      this.$refs.addCategoryDialog.openDialog();
    },
    openEditCategory(category) {
      this.$refs.editCategoryDialog.openDialog(category);
    },
    openDeleteCategory(category) {
      this.categoryToDelete = category;
      this.$refs.deleteCategoryDialog.openDialog();
    },
    openEditKeyword(keyword) {
      this.$refs.editKeywordDialog.openDialog(keyword);
    },
    onDeleteCategory() {
      this.deleteCategory(this.categoryToDelete.id)
        .then(() => {
          this.$toast.success(this.$t('page.admin-keywords.delete-category-success'));
          this.onGetKeywords().then(() => {
            if (this.languages.length > 0 && !this.languages[this.tabIndex]) this.onTabChange(0);
          });
        })
        .catch((error) => this.$toast.error(error));
      this.categoryToDelete = {};
      this.$refs.deleteCategoryDialog.closeDialog();
    },
    onAddKeywords(category) {
      if (!category || !category.id) {
        this.$toast && this.$toast.error(this.$t('page.admin-keywords.invalid-category'));
        return;
      }
      this.editKeywordsCategory = category;
      this.rawKeywords = '';
      this.transformError = '';
      this.transformState = 'idle';
      this.$nextTick(this.focusKeywordInput);
    },
    // On phones the box is in a panel, there once the panel opens (`opened`).
    focusKeywordInput() {
      const ref = this.$refs.textAreaRef;
      const el = Array.isArray(ref) ? ref[0] : ref;
      if (el && typeof el.focus === 'function') el.focus();
    },
    addKeywords() {
      const newKeywords = this.toAdd.keywords.map((value) => ({ categoryId: this.editKeywordsCategory.id, value }));
      this.addKeywordsBulk(newKeywords)
        .then(() => this.$toast.success(this.$t('page.admin-keywords.add-keywords-success')))
        .catch((error) => this.$toast.error(error));
      this.editKeywordsCategory = {};
      this.rawKeywords = '';
    },
    addTilde() {
      const textArea = this.$refs.textAreaRef[0];
      if (textArea) {
        this.rawKeywords += '~';
      }
      this.$nextTick(() => {
        if (textArea) textArea.focus();
      });
    },
    async transformOnly() {
      if (this.transformState !== 'idle') return;
      if (!this.editKeywordsCategory || !this.editKeywordsCategory.id) return;

      const hasInput = this.hasNonEmptyInput && this.keywords && this.keywords.length > 0;
      if (!hasInput) return;

      this.transformError = '';
      this.transformState = 'loading';
      try {
        const data = await keywordsService.transformKeywords(this.keywords);
        if (!Array.isArray(data)) {
          this.transformError = this.$t('error.unexpected-response');
          this.transformState = 'idle';
          return;
        }
        this.updatingFromTransform = true;
        this.rawKeywords = data.join('\n');
        this.transformState = 'done';
        this.$nextTick(() => {
          this.updatingFromTransform = false;
        });
      } catch (e) {
        const msg =
          (e && e.response && e.response.data && (e.response.data.message || e.response.data.error)) ||
          (e && e.message) ||
          'Erreur';
        this.transformError = msg;
        this.transformState = 'idle';
      }
    },

    parseEditedValues() {
      if (!this.rawKeywords) return [];
      return this.rawKeywords
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean);
    },
    async addTransformed() {
      if (!this.editKeywordsCategory || !this.editKeywordsCategory.id) {
        this.$toast && this.$toast.error(this.$t('page.admin-keywords.category-not-found'));
        return;
      }
      const values = this.parseEditedValues();
      if (!values.length) {
        this.$toast && this.$toast.info(this.$t('page.admin-keywords.no-keywords-to-add'));
        return;
      }

      const uniqueValues = Array.from(new Set(values));

      const payload = uniqueValues.map((value) => ({
        categoryId: this.editKeywordsCategory.id,
        value,
      }));

      this.addLoading = true;
      try {
        await this.addKeywordsBulk(payload);
        this.$toast && this.$toast.success(this.$t('page.admin-keywords.add-keywords-success'));

        this.editKeywordsCategory = {};
        this.rawKeywords = '';
        this.transformState = 'idle';
        await this.onRefreshKeywords();
      } catch (e) {
        const msg =
          (e && e.response && e.response.data && (e.response.data.message || e.response.data.error)) ||
          (e && e.message) ||
          'Erreur lors de l’ajout';
        this.$toast && this.$toast.error(msg);
      } finally {
        this.addLoading = false;
      }
    },
  },
  async created() {
    await this.onGetKeywords();
  },
};
</script>

<style lang="scss">
.page-keywords {
  &__content {
    display: flex;
    flex: auto;
    flex-direction: column;
    padding: 32px;

    &-main {
      background: $color-white;
      border-radius: $border-radius-root;
      box-shadow: $shadow-default;
      display: grid;
      grid-column-gap: 64px;
      grid-row-gap: 32px;
      grid-template-columns: 1fr 1fr;
      grid-template-rows: auto;
      padding: 32px;
    }
  }

  &__textarea {
    background: $color-neutral-pale;
    word-break: break-word;
    padding: 8px;
    width: 100%;
    border-radius: $border-radius-small;
    overflow: hidden;
  }

  &__add-hint {
    color: $color-text-body;
    font-size: $font-size-small;
    margin-top: 4px;

    &--error {
      color: $color-text-error;
    }
  }

  &__delete-category {
    margin-bottom: 24px;

    &--notice {
      font-weight: bold;
      font-size: 16px;
      color: $color-error;
    }
  }

  &__category-name {
    color: $color-accent;
    font-weight: bold;
  }

  &__add-keyword {
    padding-top: 8px;
    display: flex;
    justify-content: flex-end;
  }

  &__add-keyword-icon {
    height: 16px;
    width: 16px;
    padding: 0px !important;
  }

  &__errors {
    font-weight: bold;
    color: $color-error;
    display: block;
  }

  &__error {
    color: $color-error;
    display: block;
  }
  &__add-keyword-group {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    padding-top: 8px;
  }

  // Phones: the categories are cards of their own (KeywordsPhoneCategory), under the count line.
  // The language tabs on the white panel under the header, as the lists' search and filters.
  .language-tabs {
    @include phone-filter-panel {
      position: relative;
      z-index: 3;
      margin-top: -$space-xl;
      padding-top: 0;
      padding-bottom: 0;
      background: $color-white;
      box-shadow: $shadow-dropdown;
    }
  }

  @include screen-below('tablet') {
    &__count-line {
      margin-top: $space-md;
    }

    &__content-main {
      display: block;
      padding: 0;
      background: none;
      box-shadow: none;
    }
  }
  @media screen and (max-width: 1600px) {
    &__content {
      &-main {
        grid-template-columns: 1fr 1fr;
      }
    }
  }
  @media screen and (max-width: 1200px) {
    &__content {
      &-main {
        grid-template-columns: 1fr;
      }
    }
  }
}

// The add box's parts: on the page, and in the phone's panel (KeywordsAddPanel), which isn't in it.
.page-keywords,
.base-dialog__keywords-add {
  .transform-result {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .transform-result__buttons {
    display: flex;
    flex-direction: row;
    gap: 8px;
  }

  .check-slot {
    display: inline-flex;
    align-items: center;
    height: 1em;
    width: 0;
    margin-left: 0;
    overflow: hidden;
    opacity: 0;
    transition: width 180ms ease, margin-left 180ms ease, opacity 160ms ease;
  }

  .check-slot.is-visible {
    width: 22px;
    margin-left: 8px;
    opacity: 1;
  }

  .check-icon {
    display: block;
    flex: 0 0 auto;
  }
}
</style>
